import { Component, OnInit, TemplateRef } from '@angular/core';
import { NbDialogRef, NbDialogService, NbIconConfig, NbToastrService } from '@nebular/theme';
import {
  AdjustFacilityAction,
  Facility,
  FacilityActionType,
} from '../../../../@core/domain/interfaces/facility.interface';
import { FacilitiesApiService } from '../../../../@core/api/facilities-api.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { StatusPageService } from '../../services/status-page.service';
import { ParkbaseStorageService } from '../../../../@shared/services/parkbaseStorage.service';
import { Alarm, AlarmRule } from '../../../../@core/domain/interfaces/alarm.interface';
import { AlarmsApiService } from '../../../../@core/api/alarms-api.service';
import _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { LogService } from '../../../../@core';

@Component({
  selector: 'app-bypass-alarms',
  templateUrl: './bypass-alarms.component.html',
  styleUrls: ['./bypass-alarms.component.scss'],
})
export class BypassAlarmsComponent implements OnInit {
  facility!: Facility;
  canChangeBypassAlarm = false;
  bypassAlarmRules: AlarmRule[] = [];
  alarms$!: Observable<Alarm[]>;
  buttonDisabled = false;
  awaitActionComplete = false;

  successMessage!: string;
  errorMessage!: string;
  index = 0;

  private _alarmStatusActive = new BehaviorSubject<Alarm>({
    enabled: false,
    going: true,
    name: 'byPass',
  });
  readonly bypassAlarmStatusActive = this._alarmStatusActive.asObservable();
  private dialogRef!: NbDialogRef<any>;

  constructor(
    private dialogService: NbDialogService,
    private facilitiesApiService: FacilitiesApiService,
    private translate: TranslateService,
    private statusPageService: StatusPageService,
    private parkbaseStorageService: ParkbaseStorageService,
    private toastrService: NbToastrService,
    private alarmsApiService: AlarmsApiService,
    private logger: LogService
  ) {}

  ngOnInit(): void {
    this.facility = this.parkbaseStorageService.retrieveFacilitySelection();
    const actionAvailable = _.find(this.facility.availableActions, { type: 2 });
    if (actionAvailable) {
      this.alarms$ = this.alarmsApiService.alarms;
      this.alarmsApiService.loadFacilityEmergencyAlarms(this.facility.id);
      this.alarmsApiService
        .loadFacilityBypassAlarmRules(this.facility.id)
        .subscribe((alarmRules) => {
          if (alarmRules && alarmRules.length > 0) {
            this.canChangeBypassAlarm = true;
            this.bypassAlarmRules = alarmRules;
            this.alarms$.subscribe((alarms) => {
              if (alarms && alarms[3]) {
                this._alarmStatusActive.next(alarms[3]);
              }
            });
          }
        });
    }
  }

  controlBypassAlarms(dialog: TemplateRef<any>) {
    this.facility = this.parkbaseStorageService.retrieveFacilitySelection();
    this.statusPageService.triggerRefreshTerminalSummary(false);
    this.facilitiesApiService.loadTerminals(this.facility);
    this.dialogRef = this.dialogService.open(dialog, {
      context: 'this is some additional data passed to dialog',
      dialogClass: 'modal-small',
      backdropClass: 'modal-backdrop-transparent',
      hasBackdrop: true,
    });
  }

  activateBypassAlarm() {
    this.awaitActionComplete = true;
    this.buttonDisabled = true;
    const facilityAction: AdjustFacilityAction = {
      type: FacilityActionType.BypassAlarm,
      activate: true,
    };
    this.facilitiesApiService
      .sendFacilityAction(this.facility.id, facilityAction)
      .subscribe((result) => {
        if (result && result.success) {
          const iconConfig: NbIconConfig = { icon: 'checkmark-outline', pack: 'eva' };
          const alarm = { enabled: true, going: true, name: 'byPass' };
          this._alarmStatusActive.next(alarm);
          this.alarmsApiService.loadFacilityEmergencyAlarms(this.facility.id);
          this.logger.info('Bypass alarm activated');
          this.showToast(result.message, this.successMessage, {
            status: 'success',
            hasIcon: true,
            icon: iconConfig,
            duration: 5000,
            destroyByClick: true,
          });
          this.buttonDisabled = false;
          this.awaitActionComplete = false;
          if (this.dialogRef) {
            this.dialogRef.close();
          }
        } else {
          const iconConfig: NbIconConfig = { icon: 'alert-triangle-outline', pack: 'eva' };
          this.logger.error('Error activating bypass alarm', result.message);
          this.showToast(result.message, this.errorMessage, {
            status: 'danger',
            hasIcon: true,
            icon: iconConfig,
            duration: 5000,
            destroyByClick: true,
          });
        }
      });
  }

  deactivateBypassAlarm() {
    this.awaitActionComplete = true;
    this.buttonDisabled = true;
    const facilityAction: AdjustFacilityAction = {
      type: FacilityActionType.BypassAlarm,
      activate: false,
    };
    this.facilitiesApiService
      .sendFacilityAction(this.facility.id, facilityAction)
      .subscribe((result) => {
        if (result && result.success) {
          const iconConfig: NbIconConfig = { icon: 'checkmark-outline', pack: 'eva' };
          const alarm = { enabled: true, going: false, name: 'byPass' };
          this._alarmStatusActive.next(alarm);
          this.alarmsApiService.loadFacilityEmergencyAlarms(this.facility.id);
          this.logger.info('Bypass alarm deactivated');
          this.showToast(result.message, this.successMessage, {
            status: 'success',
            hasIcon: true,
            icon: iconConfig,
            duration: 5000,
            destroyByClick: true,
          });
          this.awaitActionComplete = false;
          this.buttonDisabled = false;
          if (this.dialogRef) {
            this.dialogRef.close();
          }
        } else {
          const iconConfig: NbIconConfig = { icon: 'alert-triangle-outline', pack: 'eva' };
          this.logger.error('Error deactivating bypass alarm', result.message);
          this.showToast(result.message, this.errorMessage, {
            status: 'danger',
            hasIcon: true,
            icon: iconConfig,
            duration: 5000,
            destroyByClick: true,
          });
        }
      });
  }

  private showToast(message: string, title: string, config: any): void {
    this.index += 1;
    this.toastrService.show(message, title, config);
  }
}
